import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Pencil, Send } from "lucide-react";
import {
  listEmailTemplates,
  sendAllEmailTemplateTests,
  type BulkTestResult,
} from "@/lib/email-templates.functions";
import { useAdminSession } from "@/hooks/use-admin-session";
import { confirmDialog } from "@/lib/confirm-dialog";
import { warmAdminQuery } from "@/lib/admin-loader";


export const Route = createFileRoute("/_admin/admin/emails/")({
  loader: ({ context }) => {
    warmAdminQuery(context.queryClient, {
      queryKey: ["admin", "email-templates"],
      queryFn: () => listEmailTemplates(),
    });
  },
  component: EmailTemplatesPage,
});


function EmailTemplatesPage() {
  const session = useAdminSession();
  const fetchList = useServerFn(listEmailTemplates);
  const sendAll = useServerFn(sendAllEmailTemplateTests);
  const [recipient, setRecipient] = useState(session.email ?? "");
  const [results, setResults] = useState<BulkTestResult[] | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "email-templates"],
    queryFn: () => fetchList(),
  });

  const bulkTest = useMutation({
    mutationFn: () => sendAll({ data: { recipient } }),
    onSuccess: (res: any) => {
      setResults(res.results as BulkTestResult[]);
      const failed = (res.results as BulkTestResult[]).filter((r) => !r.ok).length;
      if (failed === 0) {
        toast.success(`${res.results.length} Testmails an ${res.recipient} versendet.`);
      } else {
        toast.warning(`${failed} von ${res.results.length} Testmails fehlgeschlagen.`);
      }
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Versand fehlgeschlagen."),
  });

  return (
    <div className="mx-auto w-full max-w-5xl px-6 py-8">
      <div className="mb-6 flex items-center gap-3">
        <Mail className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
        <div>
          <h1 className="text-xl font-semibold text-foreground">E-Mails</h1>
          <p className="text-sm text-muted-foreground">
            Betreff und Inhalt aller Vorlagen pflegen. Ohne eigene Fassung wird der
            hinterlegte Standardtext versendet.
          </p>
        </div>
      </div>

      <Link
        to="/admin/emails/automationen"
        className="mb-6 inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-semibold text-foreground"
      >
        <Mail className="h-4 w-4" aria-hidden="true" /> Automationen für Begleitmails
      </Link>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Vorlagen werden geladen …
        </div>
      )}
      {error && (
        <p className="text-sm text-destructive">
          Vorlagen konnten nicht geladen werden: {(error as Error).message}
        </p>
      )}

      {session.isAdmin && (
        <section className="mb-6 rounded-lg border border-border p-4">
          <h2 className="text-sm font-semibold text-foreground">Alle Vorlagen testen</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Versendet jede Vorlage einmal mit Beispielwerten an eine interne Adresse
            (nur creategy.de). Der Betreff wird mit „[Test]" gekennzeichnet.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="name@creategy.de"
              aria-label="Empfängeradresse für Testmails"
              className="w-72 rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <button
              type="button"
              disabled={bulkTest.isPending || !recipient}
              onClick={async () => {
                const total = data?.length ?? 0;
                if (
                  await confirmDialog({
                    title: `${total} Testmails senden?`,
                    description: `Alle Vorlagen werden als Testmail an ${recipient} versendet.`,
                    confirmLabel: "Senden",
                    destructive: false,
                  })
                ) {
                  bulkTest.mutate();
                }
              }}
              className="inline-flex items-center gap-2 rounded-md bg-brand-button px-3 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
            >
              {bulkTest.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="h-4 w-4" aria-hidden="true" />
              )}
              Alle Testmails senden
            </button>
          </div>
          {results && (
            <ul className="mt-3 space-y-1 text-sm">
              {results.map((r) => (
                <li
                  key={r.key}
                  className={r.ok ? "text-muted-foreground" : "text-destructive"}
                >
                  {r.ok ? "✓" : "✗"} {r.displayName}
                  {r.error ? ` – ${r.error}` : ""}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}



      <ul className="divide-y divide-border rounded-lg border border-border">
        {(data ?? []).map((t) => (
          <li key={t.key} className="flex items-center justify-between gap-4 p-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-foreground">{t.displayName}</span>
                <span
                  className={
                    "rounded px-2 py-0.5 text-xs font-medium " +
                    (t.customized
                      ? "bg-secondary text-foreground"
                      : "bg-muted text-muted-foreground")
                  }
                >
                  {t.customized ? "Angepasst" : "Standard"}
                </span>
                {t.security && (
                  <span className="rounded bg-brand-accent/15 px-2 py-0.5 text-xs font-medium text-foreground">
                    Sicherheitsrelevant
                  </span>
                )}
              </div>
              <p className="mt-1 truncate text-sm text-muted-foreground">{t.purpose}</p>
              <p className="mt-1 truncate text-xs text-muted-foreground">
                Betreff: {t.subject}
                {t.updatedAt
                  ? ` · zuletzt geändert ${new Date(t.updatedAt).toLocaleDateString("de-DE")}${
                      t.updatedByName ? ` von ${t.updatedByName}` : ""
                    }`
                  : ""}
              </p>
            </div>
            <Link
              to="/admin/emails/$key"
              params={{ key: t.key }}
              className="inline-flex shrink-0 items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-secondary"
            >
              <Pencil className="h-4 w-4" aria-hidden="true" /> Bearbeiten
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
